import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { getMyProfile, saveMyProfile } from '../../components/apiClient.js';
import ProfileNavigation from './ProfileNavigation';

interface SleepScheduleData {
  bedTime: string;
  wakeUpTime: string;
  sleepDuration: number;
  sleepQuality: 'poor' | 'fair' | 'good' | 'excellent';
  weekendDifference: boolean;
  weekendBedTime: string;
  weekendWakeUpTime: string;
  hasNaps: boolean;
  napDuration: number;
  sleepTroubles: string[];
}

const SleepSchedule: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  // Initialize React Hook Form
  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid, isDirty },
    setValue
  } = useForm<SleepScheduleData>({
    defaultValues: {
      bedTime: '22:00',
      wakeUpTime: '06:00',
      sleepDuration: 8,
      sleepQuality: 'good',
      weekendDifference: false,
      weekendBedTime: '23:00',
      weekendWakeUpTime: '07:00',
      hasNaps: false,
      napDuration: 20,
      sleepTroubles: []
    },
    mode: 'onChange'
  });

  // Watch form values for conditional rendering
  const formData = watch();

  // Load existing sleep schedule from API
  useEffect(() => {
    const loadSleepSchedule = async () => {
      try {
        const response = await getMyProfile();
        if (response.status === 'success' && response.result) {
          const profileData = response.result;
          setProfile(profileData);

          if (profileData.sleep_pattern) {
            // Parse sleep pattern data if it exists
            const sleepData = typeof profileData.sleep_pattern === 'string'
              ? JSON.parse(profileData.sleep_pattern)
              : profileData.sleep_pattern;

            // Set form values from profile data
            setValue('bedTime', sleepData.bedTime || '22:00');
            setValue('wakeUpTime', sleepData.wakeUpTime || '06:00');
            setValue('sleepDuration', sleepData.sleepDuration || 8);
            setValue('sleepQuality', sleepData.sleepQuality || 'good');
            setValue('weekendDifference', sleepData.weekendDifference || false);
            setValue('weekendBedTime', sleepData.weekendBedTime || '23:00');
            setValue('weekendWakeUpTime', sleepData.weekendWakeUpTime || '07:00');
            setValue('hasNaps', sleepData.hasNaps || false);
            setValue('napDuration', sleepData.napDuration || 20);
            setValue('sleepTroubles', sleepData.sleepTroubles || []);
          }
        }
      } catch (error) {
        console.error('Failed to load sleep schedule:', error);
      }
    };

    loadSleepSchedule();
  }, [setValue]);

  // Form submission handler
  const onSubmit = async (data: SleepScheduleData) => {
    if (isSaving) return;

    try {
      setIsSaving(true);
      console.log('Saving sleep schedule:', data);

      // Merge form data with existing profile to prevent data loss
      const updatedProfile = {
        ...profile,
        sleep_pattern: data
      };

      const response = await saveMyProfile(updatedProfile);

      if (response.status === 'success') {
        setLastSaved(new Date());
        setProfile(updatedProfile);
      } else {
        throw new Error(response.message || 'Failed to save sleep schedule');
      }
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const sleepQualityOptions = [
    { value: 'poor', label: 'Poor', description: 'Often tired', color: 'danger', icon: 'fa-frown' },
    { value: 'fair', label: 'Fair', description: 'Sometimes tired', color: 'warning', icon: 'fa-meh' },
    { value: 'good', label: 'Good', description: 'Usually refreshed', color: 'info', icon: 'fa-smile' },
    { value: 'excellent', label: 'Excellent', description: 'Always energized', color: 'success', icon: 'fa-grin-stars' }
  ];

  const sleepTroubleOptions = [
    { id: 'difficulty_falling_asleep', label: 'Difficulty falling asleep' },
    { id: 'frequent_waking', label: 'Waking up frequently during night' },
    { id: 'early_waking', label: 'Waking up too early' },
    { id: 'nightmares', label: 'Nightmares or bad dreams' },
    { id: 'snoring', label: 'Snoring issues' },
    { id: 'restless_legs', label: 'Restless legs' },
    { id: 'stress_anxiety', label: 'Stress or anxiety' }
  ];

  return (
    <div className="min-vh-100" style={{ backgroundColor: '#f8f9ff', paddingTop: '10px' }}>
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-10 col-lg-12">
            <ProfileNavigation className="mb-4" />
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h2 className="fw-bold mb-1">
                  <i className="fas fa-bed me-2 text-primary"></i>
                  Sleep Schedule
                </h2>
                <p className="text-muted mb-0">Optimize your sleep for better study performance</p>
              </div>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigate('/profile')}
              >
                <i className="fas fa-arrow-left me-2"></i>
                Back to Profile
              </button>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="row g-4">
                {/* Sleep Times */}
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">
                        <i className="fas fa-clock me-2"></i>
                        Sleep & Wake Times
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-md-4">
                          <Controller
                            name="bedTime"
                            control={control}
                            rules={{ required: 'Please set your bedtime' }}
                            render={({ field, fieldState }) => (
                              <>
                                <label className="form-label fw-medium">Bedtime</label>
                                <input
                                  {...field}
                                  type="time"
                                  className={`form-control ${fieldState.error ? 'is-invalid' : ''}`}
                                />
                                {fieldState.error && (
                                  <div className="invalid-feedback">
                                    {fieldState.error.message}
                                  </div>
                                )}
                              </>
                            )}
                          />
                        </div>
                        <div className="col-md-4">
                          <Controller
                            name="wakeUpTime"
                            control={control}
                            rules={{ required: 'Please set your wake up time' }}
                            render={({ field, fieldState }) => (
                              <>
                                <label className="form-label fw-medium">Wake Up Time</label>
                                <input
                                  {...field}
                                  type="time"
                                  className={`form-control ${fieldState.error ? 'is-invalid' : ''}`}
                                />
                                {fieldState.error && (
                                  <div className="invalid-feedback">
                                    {fieldState.error.message}
                                  </div>
                                )}
                              </>
                            )}
                          />
                        </div>
                        <div className="col-md-4">
                          <Controller
                            name="sleepDuration"
                            control={control}
                            rules={{
                              required: 'Please set sleep duration',
                              min: { value: 4, message: 'Minimum 4 hours' },
                              max: { value: 12, message: 'Maximum 12 hours' }
                            }}
                            render={({ field, fieldState }) => (
                              <>
                                <label className="form-label fw-medium">Target Sleep Duration</label>
                                <div className="input-group">
                                  <input
                                    {...field}
                                    type="number"
                                    step="0.5"
                                    className={`form-control ${fieldState.error ? 'is-invalid' : ''}`}
                                    onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                                  />
                                  <span className="input-group-text">hours</span>
                                </div>
                                {fieldState.error && (
                                  <div className="invalid-feedback">
                                    {fieldState.error.message}
                                  </div>
                                )}
                              </>
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sleep Quality */}
                {/* <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-info text-white">
                      <h5 className="mb-0">
                        <i className="fas fa-star me-2"></i>
                        Sleep Quality
                      </h5>
                    </div>
                    <div className="card-body">
                      <Controller
                        name="sleepQuality"
                        control={control}
                        rules={{ required: 'Please rate your sleep quality' }}
                        render={({ field, fieldState }) => (
                          <>
                            <div className="row g-3">
                              {sleepQualityOptions.map((option) => (
                                <div key={option.value} className="col-md-3">
                                  <div
                                    className={`card border-2 cursor-pointer ${field.value === option.value
                                        ? `border-${option.color} bg-light`
                                        : 'border-light'
                                      }`}
                                    onClick={() => field.onChange(option.value)}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <div className="card-body text-center p-3">
                                      <div className={`rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center bg-${option.color} ${field.value === option.value ? 'text-white' : 'bg-opacity-20'}`}
                                        style={{ width: '50px', height: '50px' }}>
                                        <i className={`fas ${option.icon}`}></i>
                                      </div>
                                      <h6 className={`mb-1 ${field.value === option.value ? `text-${option.color}` : ''}`}>
                                        {option.label}
                                      </h6>
                                      <small className="text-muted">{option.description}</small>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            {fieldState.error && (
                              <div className="alert alert-danger mt-3">
                                {fieldState.error.message}
                              </div>
                            )}
                          </>
                        )}
                      />
                    </div>
                  </div>
                </div> */}

                {/* Weekend Schedule */}
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-warning text-dark">
                      <h5 className="mb-0">
                        <i className="fas fa-calendar-weekend me-2"></i>
                        Weekend Schedule
                      </h5>
                    </div>
                    <div className="card-body">
                      <Controller
                        name="weekendDifference"
                        control={control}
                        render={({ field }) => (
                          <div className="form-check form-switch mb-3">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                            />
                            <label className="form-check-label fw-medium">
                              Different schedule on weekends
                            </label>
                          </div>
                        )}
                      />

                      {formData.weekendDifference && (
                        <div className="row g-3">
                          <div className="col-md-6">
                            <Controller
                              name="weekendBedTime"
                              control={control}
                              render={({ field, fieldState }) => (
                                <>
                                  <label className="form-label fw-medium">Weekend Bedtime</label>
                                  <input
                                    {...field}
                                    type="time"
                                    className={`form-control ${fieldState.error ? 'is-invalid' : ''}`}
                                  />
                                </>
                              )}
                            />
                          </div>
                          <div className="col-md-6">
                            <Controller
                              name="weekendWakeUpTime"
                              control={control}
                              render={({ field, fieldState }) => (
                                <>
                                  <label className="form-label fw-medium">Weekend Wake Time</label>
                                  <input
                                    {...field}
                                    type="time"
                                    className={`form-control ${fieldState.error ? 'is-invalid' : ''}`}
                                  />
                                </>
                              )}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Naps & Sleep Issues */}
                {/* <div className="col-md-6">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-success text-white">
                      <h5 className="mb-0">
                        <i className="fas fa-moon me-2"></i>
                        Naps
                      </h5>
                    </div>
                    <div className="card-body">
                      <Controller
                        name="hasNaps"
                        control={control}
                        render={({ field }) => (
                          <div className="form-check form-switch mb-3">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                            />
                            <label className="form-check-label fw-medium">
                              I take naps during the day
                            </label>
                          </div>
                        )}
                      />

                      {formData.hasNaps && (
                        <Controller
                          name="napDuration"
                          control={control}
                          render={({ field, fieldState }) => (
                            <>
                              <label className="form-label fw-medium">Typical Nap Duration</label>
                              <div className="input-group">
                                <input
                                  {...field}
                                  type="number"
                                  className={`form-control ${fieldState.error ? 'is-invalid' : ''}`}
                                  onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                                />
                                <span className="input-group-text">minutes</span>
                              </div>
                            </>
                          )}
                        />
                      )}
                    </div>
                  </div>
                </div> */}

                {/* Sleep Troubles */}
                {/* <div className="col-md-6">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-danger text-white">
                      <h5 className="mb-0">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        Sleep Issues
                      </h5>
                    </div>
                    <div className="card-body">
                      <Controller
                        name="sleepTroubles"
                        control={control}
                        render={({ field }) => (
                          <div className="row g-2">
                            {sleepTroubleOptions.map((trouble) => (
                              <div key={trouble.id} className="col-12">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={trouble.id}
                                    checked={field.value.includes(trouble.id)}
                                    onChange={(e) => {
                                      const updatedTroubles = e.target.checked
                                        ? [...field.value, trouble.id]
                                        : field.value.filter(id => id !== trouble.id);
                                      field.onChange(updatedTroubles);
                                    }}
                                  />
                                  <label className="form-check-label" htmlFor={trouble.id}>
                                    {trouble.label}
                                  </label>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      />
                    </div>
                  </div>
                </div> */}
              </div>

              {/* Save Button */}
              <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                <div className="d-flex align-items-center">
                  {lastSaved && (
                    <small className="text-muted">
                      <i className="fas fa-check text-success me-1"></i>
                      Last saved: {lastSaved.toLocaleTimeString()}
                    </small>
                  )}
                  {isDirty && !lastSaved && (
                    <small className="text-warning">
                      <i className="fas fa-exclamation-triangle me-1"></i>
                      You have unsaved changes
                    </small>
                  )}
                </div>
                <button
                  type="submit"
                  className={`btn btn-primary ${isSaving ? 'disabled' : !isValid ? 'disabled' : ''
                    }`}
                  disabled={isSaving || !isValid}
                >
                  {isSaving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>
                      Save Sleep Schedule
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SleepSchedule;